import axios from 'axios';
import { z } from 'zod';

import { strictQuery } from 'utils/strict-query';

import { AddressWithNameZod } from 'types/zod/address-with-name';
import { ChangedDocumentsZod } from 'types/zod/changed-documents';
import { DocumentZod } from 'types/zod/document';
import { DocumentTypeZod } from 'types/zod/document-type';
import { NotificationsModesZod } from 'types/zod/notifications-modes';
import { ParticipantZod } from 'types/zod/participant';
import { ParticipantInDocumentZod } from 'types/zod/participant-in-document';
import { PaymentDetailsResponseZod } from 'types/zod/payment-details';
import { QInstanceStatusZod } from 'types/zod/qinstance-status';
import { QuestionnaireInstanceZod } from 'types/zod/questionnaire-instance';
import { QuestionnaireTypeCodeZod } from 'types/zod/questionnaire-type-code';
import { SummaryZod } from 'types/zod/summary';

const QInstancesApi = {
    createIncludedInstance: strictQuery({
        In: z.object({
            parent_questionnaire_instance_id: QuestionnaireInstanceZod.shape.questionnaire_instance_id,
            questionnaire_type_code: QuestionnaireInstanceZod.shape.questionnaire_type_code,
            originator_participant_id: QuestionnaireInstanceZod.shape.originator_participant_id,
        }),
        Out: QuestionnaireInstanceZod,
        handler: ({ parent_questionnaire_instance_id, questionnaire_type_code, originator_participant_id }) =>
            axios.post('/qinstances', {
                parent_questionnaire_instance_id,
                questionnaire_type_code,
                originator_participant_id,
            }),
    }),

    createInstance: strictQuery({
        In: QuestionnaireTypeCodeZod,
        Out: QuestionnaireInstanceZod,
        handler: (type) => axios.post('/qinstances', null, { params: { type } }),
    }),

    deleteInstance: strictQuery({
        In: z.object({ id: z.number(), password: z.string().optional() }),
        Out: z.any(),
        handler: ({ id, password }) => axios.delete(`/qinstances/${id}`, { params: password ? { password } : {} }),
    }),

    getDocumentsForQInstance: strictQuery({
        In: z.number(),
        Out: DocumentZod.and(z.object({ participants: z.array(ParticipantZod) })).array(),
        handler: (id) => axios.get(`/qinstances/${id}/documents`, { params: { status: 'Executed' } }),
    }),

    getDocumentTypesForQInstance: strictQuery({
        In: z.number(),
        Out: z.object({ document_types: DocumentTypeZod.array(), sign_online: z.boolean() }),
        handler: (id) => axios.get(`/qinstances/${id}/document-types-rules`),
    }),

    getInstances: strictQuery({
        In: ParticipantZod.shape.participant_id.optional(),
        Out: QuestionnaireInstanceZod.array(),
        handler: (id) => axios.get('/qinstances', { params: { participant_id: id } }),
    }),

    getJobStatus: strictQuery({
        In: z.object({ id: z.number(), job: z.number() }),
        Out: DocumentZod.array(),
        handler: ({ id, job }) => axios.get(`/qinstances/${id}/job-status`, { params: { job } }),
    }),

    getNotifiableParticipants: strictQuery({
        In: z.number(),
        Out: z.object({
            participants: ParticipantInDocumentZod.array(),
            mode: NotificationsModesZod.optional(),
        }),
        handler: (id) => axios.get(`/qinstances/${id}/notifiables`),
    }),

    getQInstance: strictQuery({
        In: QuestionnaireInstanceZod.shape.questionnaire_instance_id,
        Out: QuestionnaireInstanceZod,
        handler: (id) => axios.get(`/qinstances/${id}`),
    }),

    getSummary: strictQuery({
        In: z.object({ id: z.number(), section: z.string().optional() }),
        Out: SummaryZod,
        handler: ({ id, section }) => axios.get(`/qinstances/${id}/summary`, { params: section ? { section } : {} }),
    }),

    isPaymentOk: strictQuery({
        In: QuestionnaireInstanceZod.shape.questionnaire_instance_id,
        Out: z.object({ result: z.boolean() }),
        handler: (id) => axios.get(`/payment-is-ok/${id}`),
    }),

    loadChildren: strictQuery({
        In: z.number(),
        Out: z.object({
            questionnaires: z.record(z.string(), z.any()),
            answers: z.record(z.string(), z.any()),
        }),
        handler: (id) => axios.get(`/qinstances/${id}/children`),
    }),

    rollback: strictQuery({
        In: z.number(),
        Out: z.any(),
        handler: (id) => axios.patch(`/qinstances/${id}/rollback`),
    }),

    setLocation: strictQuery({
        In: z.object({ id: z.number(), location: z.string() }),
        Out: z.any(),
        handler: ({ id, location }) => axios.put(`/qinstances/${id}/location_description`, { value: location }),
    }),

    setStatus: strictQuery({
        In: z.object({ id: z.number(), status: QInstanceStatusZod }),
        Out: z.any(),
        handler: ({ id, status }) => axios.put(`/qinstances/${id}/status`, { value: status }),
    }),

    setToBeMailed: strictQuery({
        In: z.object({ id: z.number(), toBeMailed: z.boolean() }),
        Out: z.any(),
        handler: ({ id, toBeMailed }) => axios.put(`/qinstances/${id}/to_be_mailed/${toBeMailed}`),
    }),

    shipDocs: strictQuery({
        In: z.object({ id: z.number(), address: AddressWithNameZod }),
        Out: z.any(),
        handler: ({ id, address }) => axios.patch(`/qinstances/${id}/ship-docs`, address),
    }),

    startPayment: strictQuery({
        In: z.object({ id: z.number(), promocode: z.string().optional() }),
        Out: PaymentDetailsResponseZod,
        handler: ({ id, promocode }) => axios.get(`/qinstances/${id}/start-payment`, { params: { promocode } }),
    }),

    updateNotifiableSettings: strictQuery({
        In: z.object({ id: z.number(), options: z.any() }),
        Out: z.array(DocumentZod),
        handler: ({ id, options }) => axios.put(`/qinstances/${id}/notifiables`, options),
    }),

    getChanges: strictQuery({
        In: z.number(),
        Out: ChangedDocumentsZod,
        handler: (qInstanceId) => axios.get(`/qinstances/${qInstanceId}/changed-documents`),
    }),

    sendInvoice: strictQuery({
        In: z.number(),
        Out: z.any(),
        handler: (qInstanceId) => axios.post(`/qinstances/${qInstanceId}/send-invoice`),
    }),
};

export default QInstancesApi;
